"use client";

import { useEffect, useMemo, useState } from "react";
import { startRun, recordRunStep, completeRun } from "@/lib/actions/workshop";
import { submitReproFeedback } from "@/lib/actions/reproReview";
import { STATUS_META } from "@/lib/reproChecks";
import { CommandCardView, type CardViewData } from "@/components/CommandCardView";
import type { Enums, Json } from "@/lib/database.types";
import {
  CONTEXT_EVENT,
  CONTEXT_STORAGE_KEY,
  OS_LABELS,
  OS_VALUES,
  SHELL_LABELS,
  SHELL_VALUES,
  TARGET_LABELS,
  TARGET_VALUES,
  describeContext,
  describeSelector,
  isContextStated,
  parseStepVariants,
  recipeHasVariants,
  resolveStep,
  type OSValue,
  type ReaderContext,
  type ShellValue,
  type TargetValue,
} from "@/lib/environments";

export type StepFailureNote = {
  id: string;
  title: string;
  symptom: string;
  likely_cause: string | null;
  correction: string | null;
  still_current: boolean;
};

export type RecipeStepView = {
  id: string;
  order: number;
  kind: Enums<"recipe_step_kind">;
  title: string | null;
  body: string | null;
  expected_result: string | null;
  verification: string | null;
  optional: boolean;
  condition: string | null;
  troubleshooting: string | null;
  placeholders: Json;
  variants: Json | null;
  command_card: CardViewData | null;
  failure_notes?: StepFailureNote[];
};

export type RecipeViewData = {
  id: string;
  number: number;
  title: string;
  purpose: string | null;
  prerequisites: string | null;
  permissions_required: string | null;
  tools: Json;
  starting_state: string | null;
  est_minutes: number | null;
  success_criteria: string | null;
  rollback: string | null;
  last_tested_on: string | null;
  status: Enums<"recipe_status">;
  verified_environments: Json | null;
  steps: RecipeStepView[];
  relatedRules: { number: number; statement: string }[];
};

const STEP_STATUSES: { value: Enums<"run_step_status">; label: string; title: string }[] = [
  { value: "completed", label: "Done", title: "Completed as written" },
  { value: "skipped", label: "Skipped", title: "Not needed this time" },
  { value: "failed", label: "Failed", title: "It didn't work — say what happened in the note" },
  { value: "modified", label: "Modified", title: "Done, but not as written — note the deviation" },
  { value: "not_applicable", label: "N/A", title: "Doesn't apply in this environment" },
];

const KIND_LABEL: Record<Enums<"recipe_step_kind">, string> = {
  instruction: "Do",
  command: "Run",
  check: "Check",
  decision: "Decide",
  note: "Note",
};

/**
 * Reader-side recipe: the whole process, ordered and verifiable — read it
 * straight through, or step through it in Run Mode. A run writes a private
 * run record (who/when/step results); the published recipe is never touched
 * (Workshop principle 9).
 */
export function RecipeView({
  recipe,
  bookSlug,
  signedIn,
}: {
  recipe: RecipeViewData;
  bookSlug?: string;
  signedIn: boolean;
}) {
  const [runId, setRunId] = useState<string | null>(null);
  const [at, setAt] = useState(0);
  const [note, setNote] = useState("");
  const [results, setResults] = useState<Record<string, Enums<"run_step_status">>>({});
  const [finished, setFinished] = useState<Enums<"run_outcome"> | null>(null);
  const [starting, setStarting] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const steps = recipe.steps;
  const running = runId !== null && finished === null;

  // W8: the reader states their environment; we never sniff it. Context is
  // shared across every recipe on the page via localStorage + a window event
  // (same pattern as the nav progress bar), so setting it once applies here.
  const [ctx, setCtx] = useState<ReaderContext>({});
  const stepVariants = useMemo(
    () => steps.map((s) => parseStepVariants(s.variants)),
    [steps],
  );
  const usesVariants = useMemo(() => recipeHasVariants(stepVariants), [stepVariants]);

  useEffect(() => {
    const read = () => {
      try {
        const raw = localStorage.getItem(CONTEXT_STORAGE_KEY);
        setCtx(raw ? (JSON.parse(raw) as ReaderContext) : {});
      } catch {
        setCtx({});
      }
    };
    read();
    window.addEventListener(CONTEXT_EVENT, read);
    return () => window.removeEventListener(CONTEXT_EVENT, read);
  }, []);

  function setContext(next: ReaderContext) {
    setCtx(next);
    try {
      localStorage.setItem(CONTEXT_STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* private mode / storage disabled — context stays in memory only */
    }
    window.dispatchEvent(new CustomEvent(CONTEXT_EVENT));
  }

  async function begin() {
    setStarting(true);
    setRunError(null);
    try {
      const res = await startRun(recipe.id);
      if (res.ok && res.id) {
        setRunId(res.id);
        setAt(0);
        setResults({});
        setFinished(null);
      } else {
        setRunError("Couldn't start the run — try again.");
      }
    } catch {
      setRunError("Couldn't start the run — try again.");
    } finally {
      setStarting(false);
    }
  }

  async function mark(status: Enums<"run_step_status">) {
    if (!runId) return;
    const step = steps[at];
    setRunError(null);
    try {
      const res = await recordRunStep(runId, {
        stepId: step.id,
        order: at,
        status,
        note: note.trim() || null,
      });
      if (!res.ok) {
        setRunError("That step didn't record — check your connection and mark it again.");
        return;
      }
    } catch {
      setRunError("That step didn't record — check your connection and mark it again.");
      return;
    }
    setResults({ ...results, [step.id]: status });
    setNote("");
    if (at + 1 < steps.length) {
      setAt(at + 1);
    } else {
      // Last step marked — suggest an outcome but let the runner say it.
      setAt(steps.length);
    }
  }

  async function finish(outcome: Enums<"run_outcome">) {
    if (!runId) return;
    setRunError(null);
    try {
      const res = await completeRun(runId, outcome);
      if (res.ok) setFinished(outcome);
      else setRunError("Couldn't record the outcome — try again.");
    } catch {
      setRunError("Couldn't record the outcome — try again.");
    }
  }

  const anyFailed = Object.values(results).includes("failed");

  return (
    <div className="rounded-[12px] border border-border bg-surface p-4">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2">
        <span
          className="rounded-[6px] px-1.5 py-0.5 font-mono text-[11px] font-bold"
          style={{ background: "var(--surface-alt2)", color: "var(--copper)" }}
        >
          ❋{recipe.number}
        </span>
        <span className="min-w-0 flex-1 font-serif text-[16.5px] font-semibold text-ink">{recipe.title}</span>
        {/* W7 readiness status — earned by evidence, set by the author */}
        {recipe.status !== "draft" && (
          <span
            className="rounded-full border border-border-soft px-2 py-0.5 text-[10.5px] font-bold"
            style={{
              color: ["tested_by_author", "independently_reproduced", "verified"].includes(recipe.status)
                ? "var(--green)"
                : recipe.status === "ready_for_testing"
                  ? "var(--copper)"
                  : "#a4442c",
            }}
            title={
              STATUS_META[recipe.status].description +
              (recipe.status === "verified" && envList(recipe.verified_environments)
                ? ` Verified for: ${envList(recipe.verified_environments)}.`
                : "")
            }
          >
            {STATUS_META[recipe.status].label}
            {recipe.status === "verified" && envList(recipe.verified_environments)
              ? ` — ${envList(recipe.verified_environments)}`
              : ""}
          </span>
        )}
        {recipe.est_minutes != null && (
          <span className="rounded-full border border-border-soft px-2 py-0.5 text-[10.5px] font-bold text-muted">
            ~{recipe.est_minutes} min
          </span>
        )}
        <span className="rounded-full border border-border-soft px-2 py-0.5 text-[10.5px] font-bold text-muted">
          {steps.length} step{steps.length === 1 ? "" : "s"}
        </span>
      </div>
      {recipe.purpose && <p className="mt-1.5 text-[13px] leading-relaxed text-ink-3">{recipe.purpose}</p>}

      {/* Before you start */}
      {(recipe.prerequisites || recipe.permissions_required || recipe.starting_state || toolsText(recipe.tools)) && (
        <dl className="mt-3 grid gap-x-5 gap-y-1.5 rounded-[8px] border border-border-soft bg-paper/40 px-3 py-2.5 text-[12.5px] sm:grid-cols-2">
          {recipe.prerequisites && <MetaRow label="Prerequisites" value={recipe.prerequisites} />}
          {recipe.permissions_required && <MetaRow label="Permissions" value={recipe.permissions_required} />}
          {recipe.starting_state && <MetaRow label="Starting state" value={recipe.starting_state} />}
          {toolsText(recipe.tools) && <MetaRow label="Tools" value={toolsText(recipe.tools)!} />}
        </dl>
      )}

      {/* W8: environment context — reader-stated, always visible when the
          recipe has platform variants, so it's clear which reading you get */}
      {usesVariants && <ContextBar ctx={ctx} onChange={setContext} />}

      {/* Run bar */}
      <div className="mt-3 flex flex-wrap items-center gap-2.5">
        {!running && finished === null && (
          signedIn ? (
            <button
              onClick={begin}
              disabled={starting || steps.length === 0}
              className="rounded-[8px] bg-green px-3.5 py-1.5 text-[12.5px] font-bold text-surface disabled:opacity-40"
              title="Step through with a private record of what you did — the recipe itself is never changed"
            >
              {starting ? "Starting…" : "▶ Run this recipe"}
            </button>
          ) : (
            <a
              href={`/signin?next=/books/${bookSlug ?? ""}`}
              className="rounded-[8px] border border-border px-3.5 py-1.5 text-[12.5px] font-semibold text-green hover:border-copper"
            >
              Sign in to run this recipe
            </a>
          )
        )}
        {running && (
          <>
            <span className="text-[12.5px] font-semibold text-copper">
              Running — step {Math.min(at + 1, steps.length)} of {steps.length}
            </span>
            <button
              onClick={() => finish("abandoned")}
              className="rounded-[8px] border border-border px-3 py-1.5 text-[12px] font-semibold text-muted hover:border-copper hover:text-copper"
            >
              Abandon run
            </button>
            <span className="ml-auto text-[11.5px] text-muted-2">
              Your run is a private record — it never edits the recipe
            </span>
          </>
        )}
        {finished !== null && (
          <span className="text-[12.5px] font-semibold" style={{ color: finished === "succeeded" ? "var(--green)" : "#a4442c" }}>
            Run recorded: {finished}. Evidence from one environment — the recipe is unchanged.
          </span>
        )}
        {recipe.last_tested_on && !running && (
          <span className="ml-auto text-[11.5px] text-muted-2">Author last tested {recipe.last_tested_on}</span>
        )}
      </div>
      {runError && (
        <p className="mt-2 text-[12.5px] font-semibold" style={{ color: "#a4442c" }}>
          {runError}
        </p>
      )}

      {/* Steps */}
      <ol className="mt-3 flex flex-col gap-2">
        {steps.map((step, i) => {
          const active = running && i === at;
          const status = results[step.id];
          const dimmed = running && i > at;
          // W8: resolve how this step reads in the stated context.
          const res = resolveStep(stepVariants[i], step.body, ctx);
          const outOfScope = !res.applies;
          return (
            <li
              key={step.id}
              className={`rounded-[10px] border px-3 py-2.5 transition-colors ${
                active ? "border-copper bg-paper/70" : "border-border-soft bg-paper/30"
              } ${dimmed || outOfScope ? "opacity-55" : ""}`}
            >
              <div className="flex flex-wrap items-baseline gap-2">
                <span className={`font-mono text-[12px] font-bold ${active ? "text-copper" : "text-muted"}`}>{i + 1}.</span>
                <span
                  className="rounded-[5px] px-1.5 py-px text-[10px] font-bold uppercase tracking-[.08em]"
                  style={{ background: "var(--surface-alt2)", color: "var(--muted)" }}
                >
                  {KIND_LABEL[step.kind]}
                </span>
                {step.title && <span className="text-[14px] font-semibold text-ink">{step.title}</span>}
                {step.optional && (
                  <span className="rounded-full border border-border-soft px-1.5 py-px text-[10px] font-bold text-muted">optional</span>
                )}
                {step.condition && (
                  <span className="text-[12px] italic text-muted">only when {step.condition}</span>
                )}
                {outOfScope && (
                  <span
                    className="rounded-full border px-1.5 py-px text-[10px] font-bold"
                    style={{ borderColor: "var(--border-soft)", color: "var(--muted)" }}
                    title={`This step applies to ${describeSelector(stepVariants[i]?.appliesTo)} — not your stated environment`}
                  >
                    not in {describeContext(ctx) || "your environment"}
                  </span>
                )}
                {res.activeAlternate && (
                  <span
                    className="rounded-full px-1.5 py-px text-[10px] font-bold"
                    style={{ background: "var(--surface-alt2)", color: "var(--copper)" }}
                    title={`Showing the ${describeSelector(res.activeAlternate.match)} variant of this step`}
                  >
                    {res.activeAlternate.note || describeSelector(res.activeAlternate.match)}
                  </span>
                )}
                {status && (
                  <span className="ml-auto rounded-full border border-border-soft px-2 py-px text-[10.5px] font-bold text-muted">
                    {STEP_STATUSES.find((s) => s.value === status)?.label ?? status}
                  </span>
                )}
              </div>

              {/* Embedded command card, or plain body */}
              {step.command_card && (
                <div className="mt-2">
                  <CommandCardView card={step.command_card} bookSlug={bookSlug} compact />
                </div>
              )}
              {res.unmatched && (
                <p
                  className="mt-1.5 rounded-[8px] border px-3 py-2 text-[12px] leading-relaxed"
                  style={{ borderColor: "#a4442c55", background: "#a4442c12", color: "#a4442c" }}
                  role="note"
                >
                  No variant documented for <strong>{describeContext(ctx)}</strong> — showing the base
                  steps. Confirm they apply before you follow them.
                </p>
              )}
              {res.body && (
                <p
                  className={`mt-1.5 whitespace-pre-wrap text-[13px] leading-relaxed text-ink-2 ${
                    step.kind === "command" && !step.command_card
                      ? "overflow-x-auto rounded-[8px] border border-border-soft bg-paper/60 px-3 py-2 font-mono text-[12.5px]"
                      : ""
                  }`}
                >
                  {res.body}
                </p>
              )}

              {(step.expected_result || step.verification) && (
                <dl className="mt-1.5 grid gap-x-5 gap-y-1 text-[12.5px] sm:grid-cols-2">
                  {step.expected_result && <MetaRow label="Expect" value={step.expected_result} />}
                  {step.verification && <MetaRow label="Verify" value={step.verification} />}
                </dl>
              )}
              {(step.troubleshooting || (step.failure_notes?.length ?? 0) > 0) && (
                <details className="mt-1.5 text-[12.5px] text-ink-3">
                  <summary className="cursor-pointer font-semibold text-muted hover:text-copper">
                    If it fails here…{(step.failure_notes?.length ?? 0) > 0 && ` (${step.failure_notes!.length} known failure${step.failure_notes!.length === 1 ? "" : "s"})`}
                  </summary>
                  {step.troubleshooting && (
                    <p className="mt-1 whitespace-pre-wrap leading-relaxed">{step.troubleshooting}</p>
                  )}
                  {(step.failure_notes ?? []).map((n) => (
                    <div key={n.id} className="mt-2 rounded-[8px] border border-border-soft bg-paper/40 px-3 py-2">
                      <p className="text-[12.5px] font-bold text-ink-2">
                        ⚠ {n.title}
                        {!n.still_current && (
                          <span className="ml-2 rounded-full border border-border-soft px-1.5 py-px text-[10px] font-bold text-muted">no longer current</span>
                        )}
                      </p>
                      <p className="mt-0.5 whitespace-pre-wrap font-mono text-[11.5px] text-muted">{n.symptom}</p>
                      {n.likely_cause && <p className="mt-1"><strong className="text-muted-3">Likely cause:</strong> {n.likely_cause}</p>}
                      {n.correction && <p className="mt-0.5"><strong className="text-muted-3">Fix:</strong> {n.correction}</p>}
                    </div>
                  ))}
                </details>
              )}

              {/* Run controls on the active step */}
              {active && (
                <div className="mt-2.5 border-t border-border-soft pt-2.5">
                  <input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Note (what you saw, what you changed) — recorded with this step"
                    className="w-full rounded-[6px] border border-border-soft bg-surface px-2 py-1.5 text-[12.5px] text-ink-2 outline-none focus:outline-2 focus:outline-copper"
                  />
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {STEP_STATUSES.map((s) => (
                      <button
                        key={s.value}
                        onClick={() => void mark(s.value)}
                        title={s.title}
                        className={`rounded-[7px] px-2.5 py-1 text-[12px] font-bold ${
                          s.value === "completed"
                            ? "bg-green text-surface"
                            : "border border-border text-ink-3 hover:border-copper hover:text-copper"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ol>

      {/* Outcome picker once every step is marked */}
      {running && at >= steps.length && (
        <div className="mt-3 flex flex-wrap items-center gap-2.5 rounded-[10px] border border-border bg-paper/50 px-3 py-2.5">
          <span className="text-[13px] font-semibold text-ink">All steps marked — how did it end?</span>
          <button
            onClick={() => finish("succeeded")}
            className="rounded-[8px] bg-green px-3 py-1.5 text-[12.5px] font-bold text-surface"
          >
            Succeeded
          </button>
          <button
            onClick={() => finish("failed")}
            className="rounded-[8px] px-3 py-1.5 text-[12.5px] font-bold text-surface"
            style={{ background: "#a4442c" }}
          >
            Failed
          </button>
          {anyFailed && (
            <span className="text-[12px] text-muted">some steps failed — recorded either way</span>
          )}
        </div>
      )}

      {/* Reproduction feedback (W7): where it diverged, in which environment */}
      {finished !== null && (
        <ReproFeedbackForm
          recipeId={recipe.id}
          runId={runId}
          steps={steps}
          runOutcome={finished}
          anyDeviation={Object.values(results).some((s) => s !== "completed" && s !== "not_applicable")}
        />
      )}

      {/* Success criteria & rollback */}
      {(recipe.success_criteria || recipe.rollback) && (
        <dl className="mt-3 grid gap-x-5 gap-y-1.5 border-t border-border-soft pt-2.5 text-[12.5px] sm:grid-cols-2">
          {recipe.success_criteria && <MetaRow label="Success looks like" value={recipe.success_criteria} />}
          {recipe.rollback && <MetaRow label="Rollback" value={recipe.rollback} />}
        </dl>
      )}

      {/* Rules this recipe implements */}
      {bookSlug && recipe.relatedRules.length > 0 && (
        <p className="mt-2.5 text-[12.5px] text-muted">
          Implements{" "}
          {recipe.relatedRules.map((r, i) => (
            <span key={r.number}>
              {i > 0 && ", "}
              <a href={`/books/${bookSlug}/rules/${r.number}`} className="font-semibold text-green hover:text-copper">
                Rule {r.number}
              </a>
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

/** After a run: structured reproduction feedback for the author (W7).
 * It's evidence — the author needs it to earn "independently reproduced". */
function ReproFeedbackForm({
  recipeId,
  runId,
  steps,
  runOutcome,
  anyDeviation,
}: {
  recipeId: string;
  runId: string | null;
  steps: RecipeStepView[];
  runOutcome: Enums<"run_outcome">;
  anyDeviation: boolean;
}) {
  const [outcome, setOutcome] = useState<Enums<"repro_feedback_outcome">>(
    runOutcome === "succeeded" ? (anyDeviation ? "reproduced_with_changes" : "reproduced") : "failed",
  );
  const [stepId, setStepId] = useState("");
  const [environment, setEnvironment] = useState("");
  const [symptom, setSymptom] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(false);

  if (sent) {
    return (
      <p className="mt-3 rounded-[10px] border border-border-soft bg-paper/50 px-3 py-2.5 text-[12.5px] font-semibold text-green">
        ✓ Feedback sent to the author — thank you. It becomes reproduction evidence for this recipe.
      </p>
    );
  }

  async function send() {
    setSending(true);
    setError(false);
    try {
      const res = await submitReproFeedback(recipeId, {
        runId,
        stepId: stepId || null,
        outcome,
        environment,
        symptom,
      });
      if (res.ok) setSent(true);
      else setError(true);
    } catch {
      setError(true);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="mt-3 rounded-[10px] border border-border bg-paper/50 px-3 py-2.5">
      <p className="text-[13px] font-semibold text-ink">Tell the author how it reproduced</p>
      <p className="mt-0.5 text-[11.5px] text-muted-2">
        Structured feedback — it earns the recipe its “independently reproduced” status (or flags where it breaks).
        Don&apos;t paste secrets.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <select
          value={outcome}
          onChange={(e) => setOutcome(e.target.value as Enums<"repro_feedback_outcome">)}
          className="h-[30px] rounded-[6px] border border-border-soft bg-surface px-1.5 text-[12.5px] text-ink-2 outline-none"
          aria-label="How did it reproduce?"
        >
          <option value="reproduced">Worked as written</option>
          <option value="reproduced_with_changes">Worked, with changes</option>
          <option value="failed">Didn&apos;t work for me</option>
        </select>
        {outcome !== "reproduced" && (
          <select
            value={stepId}
            onChange={(e) => setStepId(e.target.value)}
            className="h-[30px] min-w-0 rounded-[6px] border border-border-soft bg-surface px-1.5 text-[12.5px] text-ink-2 outline-none"
            aria-label="Step where it diverged"
          >
            <option value="">Where did it diverge? (optional)</option>
            {steps.map((s, i) => (
              <option key={s.id} value={s.id}>
                Step {i + 1}{s.title ? ` — ${s.title.slice(0, 40)}` : ""}
              </option>
            ))}
          </select>
        )}
        <input
          value={environment}
          onChange={(e) => setEnvironment(e.target.value)}
          placeholder="Your environment — OS, shell, key versions"
          className="min-w-[220px] flex-1 rounded-[6px] border border-border-soft bg-surface px-2 py-1.5 text-[12.5px] text-ink-2 outline-none focus:outline-2 focus:outline-copper"
        />
      </div>
      {outcome !== "reproduced" && (
        <input
          value={symptom}
          onChange={(e) => setSymptom(e.target.value)}
          placeholder="What you saw — error message, symptom, what you changed"
          className="mt-2 w-full rounded-[6px] border border-border-soft bg-surface px-2 py-1.5 text-[12.5px] text-ink-2 outline-none focus:outline-2 focus:outline-copper"
        />
      )}
      <div className="mt-2 flex items-center gap-2.5">
        <button
          onClick={() => void send()}
          disabled={sending}
          className="rounded-[8px] bg-green px-3 py-1.5 text-[12px] font-bold text-surface disabled:opacity-50"
        >
          {sending ? "Sending…" : "Send feedback"}
        </button>
        {error && (
          <span className="text-[12px] font-semibold" style={{ color: "#a4442c" }}>
            Couldn&apos;t send — try again.
          </span>
        )}
      </div>
    </div>
  );
}

function envList(envs: Json | null): string | null {
  if (!Array.isArray(envs)) return null;
  const parts = envs.filter((e): e is string => typeof e === "string" && e.trim().length > 0);
  return parts.length ? parts.join("; ") : null;
}

function toolsText(tools: Json): string | null {
  if (!Array.isArray(tools) || tools.length === 0) return null;
  const parts = tools
    .map((t) => {
      const tool = t as { name?: string; version_range?: string };
      return [tool.name, tool.version_range].filter(Boolean).join(" ");
    })
    .filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}

/** W8: the reader states OS / shell / target; the recipe reads for that
 * context. Always visible when a recipe has variants — no auto-detection. */
function ContextBar({
  ctx,
  onChange,
}: {
  ctx: ReaderContext;
  onChange: (next: ReaderContext) => void;
}) {
  const stated = isContextStated(ctx);
  return (
    <div className="mt-3 rounded-[10px] border border-border-soft bg-paper/40 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[12.5px]">
        <span className="font-bold text-muted-3">
          {stated ? "You are reading:" : "This recipe varies by environment."}
        </span>
        {stated && (
          <span className="font-semibold text-copper">{describeContext(ctx)}</span>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <EnvSelect
            aria="Operating system"
            placeholder="OS"
            value={ctx.os ?? ""}
            options={OS_VALUES.map((v) => [v, OS_LABELS[v]])}
            onChange={(v) => onChange({ ...ctx, os: (v || undefined) as OSValue | undefined })}
          />
          <EnvSelect
            aria="Shell"
            placeholder="Shell"
            value={ctx.shell ?? ""}
            options={SHELL_VALUES.map((v) => [v, SHELL_LABELS[v]])}
            onChange={(v) => onChange({ ...ctx, shell: (v || undefined) as ShellValue | undefined })}
          />
          <EnvSelect
            aria="Target environment"
            placeholder="Target"
            value={ctx.target ?? ""}
            options={TARGET_VALUES.map((v) => [v, TARGET_LABELS[v]])}
            onChange={(v) => onChange({ ...ctx, target: (v || undefined) as TargetValue | undefined })}
          />
          {stated && (
            <button
              onClick={() => onChange({})}
              className="rounded-[6px] border border-border-soft px-2 py-1 text-[11.5px] font-semibold text-muted hover:border-copper hover:text-copper"
              title="Clear your stated environment"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      {!stated && (
        <p className="mt-1 text-[11.5px] text-muted-2">
          Set yours to see the steps for your platform — nothing is detected automatically.
        </p>
      )}
    </div>
  );
}

function EnvSelect({
  aria,
  placeholder,
  value,
  options,
  onChange,
}: {
  aria: string;
  placeholder: string;
  value: string;
  options: [string, string][];
  onChange: (v: string) => void;
}) {
  return (
    <select
      aria-label={aria}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-[28px] rounded-[6px] border border-border-soft bg-surface px-1.5 text-[12px] text-ink-2 outline-none focus:outline-2 focus:outline-copper"
      style={value ? { color: "var(--copper)", fontWeight: 600 } : undefined}
    >
      <option value="">{placeholder}: any</option>
      {options.map(([v, label]) => (
        <option key={v} value={v}>
          {label}
        </option>
      ))}
    </select>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="shrink-0 font-bold text-muted-3">{label}:</dt>
      <dd className="min-w-0 whitespace-pre-wrap text-ink-3">{value}</dd>
    </div>
  );
}
